// Sélecteur de modèles du calculateur : recherche, regroupements (catégorie, taille, nouveauté,
// popularité), pastille « tient sur ton GPU » et score ECI d'Epoch AI quand il existe. Fenêtre sous
// le champ sur ordinateur, plein écran sur téléphone (<dialog> modal : focus gardé, Échap, fond
// inerte). Remplace la liste native de ~100 modèles sans recherche (octobre 2026). La logique de la
// liste vit dans modelList.ts, la règle de la pastille dans llmVram/choices.ts (modelFit).

import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent } from "react";
import { ChevronDown, Search } from "lucide-react";
import Flag from "../components/Flag";
import { countryName, type Lab } from "./labs";
import type { ModelFit, ModelPreset } from "./llmVram";
import { compact as compactNumber } from "./catalog";
import { matchesQuery, pickerGroups, sizeLabel, type PickerGroup, type PickerMode, type PickerRow } from "./modelList";

const PHONE = "(max-width: 640px)";
const subscribePhone = (cb: () => void) => {
  const mq = window.matchMedia(PHONE);
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
};
const MODES: [PickerMode, string][] = [["type", "Type"], ["size", "Size"], ["new", "New"], ["popular", "Popular"]];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Props = {
  /** id du bouton (le label du champ pointe dessus). */
  id: string;
  value: string;
  models: ModelPreset[];
  /** Catégories du site, dans leur ordre. */
  groupOrder: string[];
  /** Modèle chargé depuis Hugging Face : en tête, sous ce titre. */
  extra?: ModelPreset;
  extraTitle: string;
  customId: string;
  customLabel: string;
  labelOf: (m: ModelPreset) => string;
  labOf: (m: ModelPreset) => Lab | undefined;
  downloadsOf: (m: ModelPreset) => number;
  eciOf: (m: ModelPreset) => number | undefined;
  isNew: (m: ModelPreset) => boolean;
  /** Absent (entraînement) : pas de pastille ni de filtre. */
  fitOf?: (m: ModelPreset) => ModelFit;
  /** Résume ce dont fitOf dépend : les pastilles ne sont recalculées que s'il change. */
  fitKey: string;
  /** « 4-bit (Q4_K_M), 32K, 1 request » : sur quoi portent les pastilles. */
  fitBasis: string;
  gpuName: string;
  gpuCount: number;
  onChange: (id: string) => void;
};

const plain = (label: string) => label.replace(/ \(MoE\)$/, "");

function fitText(f: ModelFit, gpu: string, n: number) {
  const on = n > 1 ? `${n} × ${gpu}` : `one ${gpu}`;
  if (f.status === "ok") return `fits on ${on}`;
  if (f.status === "ram") return `fits on ${on} with system RAM`;
  if (f.status === "multi") return `needs ${f.gpus} × ${gpu}`;
  return `does not fit on 8 × ${gpu}`;
}

function FitBadge({ fit }: { fit: ModelFit }) {
  const sym = fit.status === "ok" ? "✓" : fit.status === "ram" ? "◐" : fit.status === "multi" ? `×${fit.gpus}` : "✕";
  return <span className={`vram-mp-fit ${fit.status}`} aria-hidden="true">{sym}</span>;
}

export default function ModelPicker(p: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<PickerMode>("type");
  const [fitsOnly, setFitsOnly] = useState(false);
  const [active, setActive] = useState(0);
  const phone = useSyncExternalStore(subscribePhone, () => window.matchMedia(PHONE).matches, () => false);
  const uid = useId(), listId = `${uid}-list`, titleId = `${uid}-title`;
  const optId = (i: number) => `${uid}-opt-${i}`;
  const wrapRef = useRef<HTMLDivElement>(null), triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null), dialogRef = useRef<HTMLDialogElement>(null);

  const all = useMemo(() => (p.extra ? [p.extra, ...p.models] : p.models), [p.extra, p.models]);
  // Pastilles : une centaine de calculs, faits à l'ouverture et quand les réglages changent.
  const { fitOf, fitKey } = p;
  const fits = useMemo(() => (open && fitOf ? new Map(all.map((m) => [m.id, fitOf(m)])) : null),
    // fitOf change à chaque rendu de la page ; fitKey résume ce dont il dépend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, fitKey, all]);

  const groupsFor = (q: string, m: PickerMode, onlyFits: boolean): PickerGroup[] => {
    const rowOf = (x: ModelPreset): PickerRow => ({ m: x, label: plain(p.labelOf(x)), lab: p.labOf(x)?.name, downloads: p.downloadsOf(x) });
    const keep = (r: PickerRow) => matchesQuery(r, q) && (!onlyFits || ["ok", "ram"].includes(fits?.get(r.m.id)?.status ?? "ok"));
    const extra = p.extra ? [rowOf(p.extra)].filter(keep) : [];
    return [
      ...(extra.length ? [{ title: p.extraTitle, rows: extra }] : []),
      ...pickerGroups(p.models.map(rowOf).filter(keep), m, p.groupOrder),
    ];
  };
  const groups = open ? groupsFor(query, mode, fitsOnly) : [];
  const flat = groups.flatMap((g) => g.rows);

  const close = (focusTrigger: boolean) => {
    // Le <dialog> modal rend la page inerte : le fermer d'abord, sinon le bouton ne reprend pas le focus.
    if (dialogRef.current?.open) dialogRef.current.close();
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus({ preventScroll: true });
  };
  // Fait défiler la liste seule (scrollIntoView ferait aussi défiler la page), sous l'en-tête de groupe collant.
  const scrollToOption = (index: number, center: boolean) => {
    const list = document.getElementById(listId), el = document.getElementById(optId(index));
    if (!list || !el) return;
    const head = list.querySelector<HTMLElement>(".vram-mp-group")?.offsetHeight ?? 0;
    const top = el.offsetTop, bottom = top + el.offsetHeight;
    if (center) list.scrollTop = top - (list.clientHeight - el.offsetHeight) / 2;
    else if (top - head < list.scrollTop) list.scrollTop = top - head;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  };
  const openPicker = () => {
    const rows = groupsFor("", mode, fitsOnly).flatMap((g) => g.rows);
    setQuery("");
    setActive(Math.max(0, rows.findIndex((r) => r.m.id === p.value)));
    setOpen(true);
  };
  const pick = (id: string) => {
    p.onChange(id);
    close(true);
  };
  const reset = (next: () => void) => { next(); setActive(0); };

  // Ouverture : la recherche prend le focus, le modèle choisi est centré dans la liste. Téléphone :
  // plein écran modal, la page derrière ne défile plus.
  useEffect(() => {
    if (!open) return;
    const d = dialogRef.current;
    if (phone && d && !d.open) d.showModal();
    if (!phone) {
      const below = window.innerHeight - (triggerRef.current?.getBoundingClientRect().bottom ?? 0);
      const need = Math.min(568, window.innerHeight * 0.7 + 8) + 16;
      if (below < need) window.scrollBy({ top: need - below, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    }
    if (phone) document.documentElement.classList.add("vram-mp-lock");
    inputRef.current?.focus({ preventScroll: true });
    scrollToOption(active, true);
    return () => document.documentElement.classList.remove("vram-mp-lock");
    // Seulement à l'ouverture (et au passage ordinateur ↔ téléphone).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phone]);
  useEffect(() => {
    if (open) scrollToOption(active, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  // Ordinateur : un clic ailleurs ferme la fenêtre.
  useEffect(() => {
    if (!open || phone) return;
    const away = (e: PointerEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open, phone]);

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const move = (d: number) => { e.preventDefault(); if (flat.length) setActive((a) => Math.max(0, Math.min(flat.length - 1, a + d))); };
    if (e.key === "ArrowDown") move(1);
    else if (e.key === "ArrowUp") move(-1);
    else if (e.key === "PageDown") move(8);
    else if (e.key === "PageUp") move(-8);
    else if (e.key === "Enter") { e.preventDefault(); if (flat[active]) pick(flat[active].m.id); }
    else if (e.key === "Escape") { e.preventDefault(); close(true); }
  };

  const selected = all.find((m) => m.id === p.value);
  const selLabel = selected ? plain(p.labelOf(selected)) : p.customLabel;
  const selFit = selected && p.fitOf ? p.fitOf(selected) : undefined;
  const anyEci = flat.some((r) => p.eciOf(r.m) !== undefined);

  let i = 0;
  const panel = (
    <>
      {phone && (
        <div className="vram-mp-sheethead">
          <b id={titleId}>Pick a model</b>
          <button type="button" className="vram-mp-close" onClick={() => close(true)}>Close</button>
        </div>
      )}
      <div className="vram-mp-top">
        <label className="vram-mp-search">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Search a model</span>
          <input
            ref={inputRef} type="search" role="combobox" aria-expanded="true" aria-controls={listId} aria-autocomplete="list"
            aria-activedescendant={flat.length ? optId(active) : undefined} autoComplete="off" spellCheck={false} enterKeyHint="go"
            placeholder="Search: qwen 27, gemma, coder…" value={query}
            onChange={(e) => reset(() => setQuery(e.target.value))} onKeyDown={onKey}
          />
        </label>
        <div className="vram-mp-tools">
          <div className="vram-mp-seg" role="group" aria-label="Group by">
            {MODES.map(([m, label]) => (
              <button key={m} type="button" aria-pressed={mode === m} onClick={() => reset(() => setMode(m))}>{label}</button>
            ))}
          </div>
          {fits && (
            <button type="button" className="vram-mp-chip" aria-pressed={fitsOnly} onClick={() => reset(() => setFitsOnly(!fitsOnly))}>
              Runs on my {p.gpuName}
            </button>
          )}
        </div>
      </div>
      <ul id={listId} role="listbox" aria-label="Models" className="vram-mp-list">
        {flat.length === 0 && (
          <li className="vram-mp-empty" role="presentation">
            <b>{query ? `No model matches “${query}”` : `No model runs on one ${p.gpuName} with these settings`}</b>
            <span>{query ? "Paste its Hugging Face link in the field below instead." : "Turn off the filter, or lower the context."}</span>
          </li>
        )}
        {groups.map((g, gi) => (
          <li key={`g:${g.title}`} role="presentation">
            {/* Un groupe par catégorie : son en-tête reste en haut tant qu'on est dedans. */}
            <ul role="group" aria-labelledby={`${uid}-g${gi}`} className="vram-mp-gul">
              <li className="vram-mp-group" role="presentation" id={`${uid}-g${gi}`}>
                <b>{g.title}</b><span>{g.hint ? `${g.hint} · ` : ""}{g.rows.length}</span>
              </li>
              {g.rows.map((r) => {
                const idx = i++, m = r.m, fit = fits?.get(m.id), lab = p.labOf(m), eci = p.eciOf(m);
                const sub = mode === "popular" ? `${compactNumber(r.downloads)} downloads`
                  : mode === "new" && m.released ? `${Number(m.released.slice(8, 10))} ${MONTHS_SHORT[Number(m.released.slice(5, 7)) - 1]}`
                  : m.activeParams ? `${sizeLabel(m.activeParams)} active` : "";
                return (
                  <li
                    key={m.id} id={optId(idx)} role="option" aria-selected={m.id === p.value} data-active={idx === active}
                    className="vram-mp-opt" onClick={() => pick(m.id)} onMouseMove={() => idx !== active && setActive(idx)}
                  >
                    {fit && <FitBadge fit={fit} />}
                    <span className="vram-mp-main">
                      <span className="vram-mp-line">
                        <span className="vram-mp-name">{r.label}</span>
                        {p.isNew(m) && <span className="vram-mp-new">New</span>}
                      </span>
                      <span className="vram-mp-meta">
                        {lab && <><Flag country={lab.country} />{lab.name}</>}
                        {mode !== "type" && m.group ? `${lab ? " · " : ""}${m.group}` : ""}
                      </span>
                    </span>
                    <span className="vram-mp-side">
                      <span className="vram-mp-size">{sizeLabel(m.params)}</span>
                      {sub && <span className="vram-mp-sub">{sub}</span>}
                      {eci !== undefined && <span className="vram-mp-eci">ECI {eci}</span>}
                    </span>
                    <span className="sr-only">
                      {`, ${sizeLabel(m.params)}${m.activeParams ? `, ${sizeLabel(m.activeParams)} active` : ""}`}
                      {lab?.country ? `, ${countryName(lab.country)}` : ""}
                      {fit ? `, ${fitText(fit, p.gpuName, p.gpuCount)}` : ""}
                      {eci !== undefined ? `, Epoch capabilities index ${eci}` : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      <div className="vram-mp-foot">
        <span>{flat.length} model{flat.length === 1 ? "" : "s"}</span>
        {fits && <span>✓ fits · ◐ with RAM · ×N GPUs · ✕ no, at {p.fitBasis}</span>}
        {anyEci && <span>ECI: <a href="https://epoch.ai/benchmarks" target="_blank" rel="noopener noreferrer">Epoch AI</a>, CC BY</span>}
        {!phone && <span className="vram-mp-keys"><kbd>↑</kbd> <kbd>↓</kbd> move · <kbd>Enter</kbd> pick · <kbd>Esc</kbd> close</span>}
        <button type="button" className="vram-mp-custom" onClick={() => pick(p.customId)}>{p.customLabel}…</button>
      </div>
    </>
  );

  return (
    <div className="vram-mp" ref={wrapRef}>
      <button
        ref={triggerRef} type="button" id={p.id} className="vram-mp-trigger" aria-haspopup="dialog" aria-expanded={open}
        // Le nom accessible commence par le texte visible (WCAG 2.5.3, « label in name ») : le modèle, puis l'action.
        aria-label={`${selLabel}${selFit ? `, ${fitText(selFit, p.gpuName, p.gpuCount)}` : ""}. Pick a model`}
        onClick={() => (open ? close(false) : openPicker())}
      >
        {selFit && <FitBadge fit={selFit} />}
        {/* Nom et taille sur une ligne ; sur téléphone, la taille passe dessous plutôt que de rogner le nom. */}
        <span className="vram-mp-ttext">
          <span className="vram-mp-tname">{selLabel}</span>
          {selected && (
            <span className="vram-mp-tsize">
              {sizeLabel(selected.params)}{selected.activeParams ? ` · ${sizeLabel(selected.activeParams)} active` : ""}
            </span>
          )}
        </span>
        <ChevronDown className="vram-mp-chev" size={18} aria-hidden="true" />
      </button>
      {open && !phone && <div className="vram-mp-pop" role="dialog" aria-label="Pick a model">{panel}</div>}
      {open && phone && (
        <dialog
          ref={dialogRef} className="vram-mp-sheet" aria-labelledby={titleId}
          onCancel={(e) => { e.preventDefault(); close(true); }}
        >
          {panel}
        </dialog>
      )}
    </div>
  );
}
